/**
 * Reusable View Dialog Component
 * Displays detailed information for bottles, caps, labels, and supplies
 */

import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Card, CardContent, Grid, Stack, IconButton, alpha, useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  FiberManualRecord as FiberManualRecordIcon
} from '@mui/icons-material';

export default function ViewDialogue({
  open,
  onClose,
  item,
  itemType,
  onEdit
}) {
  const theme = useTheme();

  if (!item) return null;

  const typeLabels = {
    bottles: 'Bottle',
    caps: 'Cap',
    labels: 'Label',
    supplies: 'Supply'
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          maxHeight: '90vh',
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
          bgcolor: alpha(theme.palette.grey[50], 0.95),
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        pt: 3,
        px: 3,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <FiberManualRecordIcon sx={{ color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              {typeLabels[itemType]} Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              {item.name}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.grey[400], 0.1),
              color: 'text.primary'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Basic Information Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Basic Information
                  </Typography>
                  <Stack spacing={2}>
                    {item.sku && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          SKU
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.sku}
                        </Typography>
                      </Box>
                    )}
                    {item.description && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Description
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    )}
                    {item.material && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Material
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.material}
                        </Typography>
                      </Box>
                    )}
                    {item.color && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Color
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.color}
                        </Typography>
                      </Box>
                    )}
                    {item.closure_type && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Closure Type
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.closure_type}
                        </Typography>
                      </Box>
                    )}
                    {item.finish && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Finish
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.finish}
                        </Typography>
                      </Box>
                    )}
                    {item.neck_finish && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Neck Finish
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.neck_finish}
                        </Typography>
                      </Box>
                    )}
                    {item.shape && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Shape
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.shape}
                        </Typography>
                      </Box>
                    )}
                    {item.category && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Category
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.category}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Physical Properties Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Physical Properties
                  </Typography>
                  <Stack spacing={2}>
                    {/* Capacity - bottles/caps */}
                    {((item.capacity !== null && item.capacity !== undefined) || (item.capacity_value !== null && item.capacity_value !== undefined)) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Capacity
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.capacity || item.capacity_value}{item.capacity_unit || 'ml'}
                        </Typography>
                      </Box>
                    )}
                    {/* Height - bottles/caps */}
                    {(item.height_value !== null && item.height_value !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Height
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.height_value}{item.height_unit || 'mm'}
                        </Typography>
                      </Box>
                    )}
                    {/* Height - labels */}
                    {(item.height_mm !== null && item.height_mm !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Height
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.height_mm}{item.dimensions_unit || 'mm'}
                        </Typography>
                      </Box>
                    )}
                    {/* Diameter - bottles/caps */}
                    {(item.diameter_value !== null && item.diameter_value !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Diameter
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.diameter_value}{item.diameter_unit || 'mm'}
                        </Typography>
                      </Box>
                    )}
                    {/* Width - bottles/caps */}
                    {(item.width_value !== null && item.width_value !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Width
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.width_value}{item.width_unit || 'mm'}
                        </Typography>
                      </Box>
                    )}
                    {/* Width - labels */}
                    {(item.width_mm !== null && item.width_mm !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Width
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.width_mm}{item.dimensions_unit || 'mm'}
                        </Typography>
                      </Box>
                    )}
                    {/* Length - bottles/caps */}
                    {(item.length_value !== null && item.length_value !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Length
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.length_value}{item.length_unit || 'mm'}
                        </Typography>
                      </Box>
                    )}
                    {/* Weight */}
                    {(item.weight_g !== null && item.weight_g !== undefined || item.weight !== null && item.weight !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Weight
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.weight_g || item.weight}g
                        </Typography>
                      </Box>
                    )}
                    {/* Adhesive Type - labels */}
                    {item.adhesive_type && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Adhesive Type
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.adhesive_type}
                        </Typography>
                      </Box>
                    )}
                    {/* Special Features - labels */}
                    {item.special_features && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Special Features
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.special_features}
                        </Typography>
                      </Box>
                    )}
                    {/* Supplies - Dynamic Attributes */}
                    {[1,2,3,4,5,6,7,8,9,10].map(num => {
                      const label = item[`attribute_${num}_label`];
                      const value = item[`attribute_${num}_value`];
                      const unit = item[`attribute_${num}_unit`];
                      if (label && value) {
                        return (
                          <Box key={num}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              {label}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {value}{unit || ''}
                            </Typography>
                          </Box>
                        );
                      }
                      return null;
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Business Information Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Business Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Vendor
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.supplier?.name || 'No vendor assigned'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Unit Cost
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        ${item.unit_cost?.toFixed(2) || '0.00'} per {item.unit || 'unit'}
                      </Typography>
                    </Box>
                    {(item.moq !== null && item.moq !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Minimum Order Quantity
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.moq} {item.unit || 'units'}
                        </Typography>
                      </Box>
                    )}
                    {(item.lead_time_days !== null && item.lead_time_days !== undefined || item.lead_time !== null && item.lead_time !== undefined) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Lead Time
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.lead_time_days || item.lead_time} days
                        </Typography>
                      </Box>
                    )}
                    {(item.pack_quantity || item.pack_qty) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Pack Quantity
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.pack_quantity || item.pack_qty}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Information Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                    Additional Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Created
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Last Updated
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                        Notes
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.notes || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        background: 'white'
      }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => {
            onClose();
            onEdit(item);
          }}
          sx={{
            borderRadius: 2,
            px: 3,
            bgcolor: 'black',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
          }}
        >
          Edit {typeLabels[itemType]}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
